import { useState, useEffect, useCallback } from 'react';
import { Room, LocalVideoTrack } from 'twilio-video';

export const useCameraDevices = (room: Room | undefined) => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [activeCameraId, setActiveCameraId] = useState<string>();

  const load = useCallback(async () => {
    await navigator.mediaDevices.getUserMedia({ video: true }).catch(() => {});
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cams = devices.filter((d) => d.kind === 'videoinput');
    setCameras(cams);

    const currentTrack = room
      ? Array.from(room.localParticipant.videoTracks.values()).find(
          (pub) => pub.track.name !== 'screen-share'
        )?.track
      : undefined;
    if (currentTrack) {
      setActiveCameraId(currentTrack.mediaStreamTrack.getSettings().deviceId);
    } else if (cams.length > 0) {
      setActiveCameraId(cams[0].deviceId);
    }
  }, [room]);

  const switchCamera = useCallback(
    async (deviceId: string) => {
      if (!room) return;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });

      const newTrack = new LocalVideoTrack(stream.getVideoTracks()[0]);

      const publications = Array.from(room.localParticipant.videoTracks.values()).filter(
        (pub) => pub.track.name !== 'screen-share'
      );
      for (const pub of publications) {
        pub.track.stop();
        room.localParticipant.unpublishTrack(pub.track);
      }

      await room.localParticipant.publishTrack(newTrack);
      setActiveCameraId(deviceId);
    },
    [room]
  );

  useEffect(() => {
    load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => navigator.mediaDevices.removeEventListener('devicechange', load);
  }, [load]);

  return { cameras, activeCameraId, switchCamera };
};
